import { Trans, useLingui } from '@lingui/react/macro';
import type { Diagnostic } from '@repo/contracts';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  blockingRulesForShift,
  type ProposalShiftCell as ProposalShiftCellData,
} from '@/domain/rotagen/proposal-day-data';
import { formatTime } from '@/domain/templates/preview-utils';

import { ShiftPill } from './shift-pill';

interface ProposalShiftCellProps {
  /** The shift + its fill state for this slot. */
  cell: ProposalShiftCellData;
  /** Diagnostics referencing this shift, used for the per-shift "why". */
  diagnostics: Diagnostic[];
}

/**
 * One slot in the read-only proposal grid: a per-person pill (the assigned
 * worker, or an unfilled "short" pill) that opens a popover explaining the
 * shift — its fill state and, when unfilled, the distinct scheduling rules that
 * blocked candidates (humanised, deduplicated — never a wall of raw messages).
 */
export function ProposalShiftCell({ cell, diagnostics }: ProposalShiftCellProps) {
  const { t } = useLingui();
  const { shift, worker, filled } = cell;
  const time = `${formatTime(shift.start)}–${formatTime(shift.end)}`;
  const blockingRules = filled ? [] : blockingRulesForShift(diagnostics, shift.id);

  return (
    <Popover>
      <PopoverTrigger className="w-full text-left">
        <ShiftPill
          type={shift.shiftType.name}
          time={time}
          short={!filled}
          subtitle={filled ? worker?.name : t`Short`}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 space-y-2 text-[length:var(--body-font-size)] leading-[var(--body-line-height)]"
      >
        <div>
          <p className="font-semibold">{shift.shiftType.name}</p>
          <p className="text-[var(--muted-foreground)]">{time}</p>
        </div>
        {filled ? (
          <p>
            <Trans>Assigned to {worker?.name}</Trans>
          </p>
        ) : (
          <>
            <p className="font-medium text-destructive">
              <Trans>Unfilled — no one was assigned.</Trans>
            </p>
            {blockingRules.length > 0 && (
              <div className="border-t pt-2 text-[var(--muted-foreground)]">
                <span className="font-medium">
                  <Trans>Blocked by:</Trans>
                </span>{' '}
                {blockingRules.join(', ')}
              </div>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
